import { captureException } from "@utils/captureException";

export type RevenueCatSubscribeResult = {
  success: boolean;
  error?: string;
  errorCode?: string;
  pending?: boolean;
  userCancelled?: boolean;
};

export type RevenueCatErrorDetails = {
  message: string;
  readableErrorCode?: string;
  backendErrorCode?: string | number;
  userCancelled: boolean;
};

export type RevenueCatSubscribeFailureToast = {
  header: string;
  message: string;
  type: "error" | "info";
};

export type RevenueCatSurface =
  | "startup"
  | "paywall"
  | "settings-subscription"
  | "delete-account"
  | "onboarding-loading"
  | "unknown";

type RevenueCatReportChannel = "error" | "warning" | "breadcrumb" | "ignore";
type RevenueCatPaywallPhase =
  | "offering"
  | "purchase"
  | "entitlement"
  | "user_update";

const RECEIPT_ALREADY_IN_USE_CODE = "RECEIPT_ALREADY_IN_USE";
const OFFLINE_CONNECTION_ERROR_CODE = "OFFLINE_CONNECTION_ERROR";
const PAYMENT_PENDING_ERROR_CODE = "PAYMENT_PENDING_ERROR";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : undefined;

const readUserInfoValue = (
  userInfo: Record<string, unknown>,
  key: string,
): unknown => {
  if (key in userInfo) {
    return userInfo[key];
  }

  const underlyingError = userInfo.NSUnderlyingError;
  if (isRecord(underlyingError) && key in underlyingError) {
    return underlyingError[key];
  }

  return undefined;
};

export const isReceiptAlreadyInUseError = (
  details: Pick<RevenueCatErrorDetails, "readableErrorCode" | "message">,
) =>
  details.readableErrorCode === RECEIPT_ALREADY_IN_USE_CODE ||
  details.message.includes(
    "already another active subscriber using the same receipt",
  );

export const isRevenueCatOfflineError = (
  details: Pick<RevenueCatErrorDetails, "readableErrorCode" | "message">,
) =>
  details.readableErrorCode === OFFLINE_CONNECTION_ERROR_CODE ||
  details.message
    .toLowerCase()
    .includes("internet connection appears to be offline");

// `PURCHASES_ERROR_CODE` NETWORK_ERROR ("10") and OFFLINE_CONNECTION_ERROR ("35").
const NETWORK_ERROR_CODES = new Set([
  "10",
  "35",
  "NETWORK_ERROR",
  OFFLINE_CONNECTION_ERROR_CODE,
]);

/** RevenueCat could not reach its servers: offline, or the request failed in transit. */
export const isRevenueCatNetworkError = (error: unknown) => {
  if (!isRecord(error)) return false;
  const userInfo = isRecord(error.userInfo) ? error.userInfo : {};
  const details = getRevenueCatErrorDetails(error);
  return (
    [
      error.code,
      error.readableErrorCode,
      userInfo.readableErrorCode,
      details.readableErrorCode,
    ].some(
      (code) => typeof code === "string" && NETWORK_ERROR_CODES.has(code),
    ) || isRevenueCatOfflineError(details)
  );
};

export const isPaymentPendingError = (
  details: Pick<RevenueCatErrorDetails, "readableErrorCode" | "message">,
) =>
  details.readableErrorCode === PAYMENT_PENDING_ERROR_CODE ||
  details.message.toLowerCase().includes("payment is deferred");

export const getRevenueCatErrorDetails = (
  error: unknown,
): RevenueCatErrorDetails => {
  if (!isRecord(error)) {
    return {
      message: String(error),
      userCancelled: false,
    };
  }

  const userCancelled = error.userCancelled === true;
  const userInfo = isRecord(error.userInfo) ? error.userInfo : null;
  const message =
    nonEmptyString(error.message) ??
    nonEmptyString(error.localizedDescription) ??
    nonEmptyString(error.underlyingErrorMessage) ??
    nonEmptyString(userInfo?.NSLocalizedDescription) ??
    nonEmptyString(
      readUserInfoValue(userInfo ?? {}, "NSLocalizedDescription"),
    ) ??
    "Unknown RevenueCat error";
  const readableErrorCode =
    nonEmptyString(userInfo?.readable_error_code) ??
    nonEmptyString(readUserInfoValue(userInfo ?? {}, "readable_error_code")) ??
    nonEmptyString(error.code);

  const backendErrorCodeRaw =
    userInfo?.rc_backend_error_code ??
    readUserInfoValue(userInfo ?? {}, "rc_backend_error_code");

  const backendErrorCode =
    typeof backendErrorCodeRaw === "string" ||
    typeof backendErrorCodeRaw === "number"
      ? backendErrorCodeRaw
      : undefined;

  return {
    message,
    readableErrorCode,
    backendErrorCode,
    userCancelled,
  };
};

export const reportRevenueCatError = (
  error: unknown,
  {
    area = "subscription",
    channel = "error",
    extra,
    fingerprint,
    operation,
    surface = "unknown",
    skipWhenUserCancelled = false,
    tags,
  }: {
    area?: string;
    channel?: RevenueCatReportChannel;
    extra?: Record<string, unknown>;
    fingerprint?: string[];
    operation: string;
    surface?: RevenueCatSurface;
    skipWhenUserCancelled?: boolean;
    tags?: Record<string, string>;
  },
) => {
  const details = getRevenueCatErrorDetails(error);

  if (skipWhenUserCancelled && details.userCancelled) {
    return;
  }

  const reportableError =
    error instanceof Error
      ? error
      : Object.assign(new Error(details.message), { name: "RevenueCatError" });

  captureException(reportableError, {
    channel,
    area,
    ...(fingerprint ? { fingerprint } : {}),
    operation,
    tags: {
      revenuecat_surface: surface,
      ...tags,
      ...(details.readableErrorCode
        ? { rc_error_code: details.readableErrorCode }
        : {}),
      ...(details.backendErrorCode != null
        ? { rc_backend_error_code: String(details.backendErrorCode) }
        : {}),
      ...(details.userCancelled ? { rc_user_cancelled: "true" } : {}),
    },
    extra: {
      revenueCat: {
        message: details.message,
        errorCode: details.readableErrorCode,
        backendErrorCode: details.backendErrorCode,
        userCancelled: details.userCancelled,
      },
      originalError: error,
      ...extra,
    },
  });
};

export const getSubscribeFailureToast = (
  result: RevenueCatSubscribeResult,
): RevenueCatSubscribeFailureToast | null => {
  if (result.success || result.userCancelled) {
    return null;
  }

  if (result.pending) {
    return {
      header: "Payment pending",
      message:
        "Your payment is waiting for approval. Premium will unlock after approval once your subscription status updates.",
      type: "info",
    };
  }

  if (result.errorCode === RECEIPT_ALREADY_IN_USE_CODE) {
    return {
      header: "Subscription already active",
      message:
        "This Apple ID already has Lissen Premium on another account. Sign in with that account, or contact support if you need help moving your subscription.",
      type: "error",
    };
  }

  return {
    header: "Subscription failed",
    message: "Something went wrong. Please try again.",
    type: "error",
  };
};

export const toSubscribeErrorResult = (
  error: unknown,
  {
    operation = "revenuecat-subscribe",
    phase = "purchase",
    surface = "unknown",
  }: {
    operation?: string;
    phase?: RevenueCatPaywallPhase;
    surface?: RevenueCatSurface;
  } = {},
): RevenueCatSubscribeResult => {
  const details = getRevenueCatErrorDetails(error);
  const pending = isPaymentPendingError(details);

  if (!pending) {
    reportRevenueCatError(error, {
      ...(surface === "paywall"
        ? {
            fingerprint: [
              "paywall",
              phase,
              details.readableErrorCode ?? "unknown",
            ],
            area: "paywall",
            operation,
            tags: {
              paywall_phase: phase,
            },
          }
        : { operation }),
      surface,
      skipWhenUserCancelled: true,
    });
  }

  return {
    success: false,
    error: details.message,
    pending,
    errorCode: isReceiptAlreadyInUseError(details)
      ? RECEIPT_ALREADY_IN_USE_CODE
      : details.readableErrorCode,
    userCancelled: details.userCancelled,
  };
};
